import { Project, Model3DElement } from '../types';
import CryptoJS from 'crypto-js';

/**
 * Kidata文件格式
 * 包含3D家具模型的位置、旋转、缩放、颜色信息
 * 以及楼层障碍物数据（墙面、矩形、圆形等）
 */
export interface KidataFurniture {
  id: string; // 模型文件名（不含扩展名，如 Koltuk），SDK 会从 kidata.models[modelRef] 读取源文件
  type: string; // 模型分类（文件夹名，如 Sofa）
  modelRef?: string; // 指向 kidata.models[hash]（内嵌模型源文件的 hash key）
  position: { x: number; y: number; z: number };
  rotation: { x: number; y: number; z: number };
  scale: { x: number; y: number; z: number };
  color?: string; // 模型颜色（十六进制，如 #888888）
  targetSize?: { width: number; height: number; depth: number }; // 目标尺寸（米），用于SDK归一化缩放
  elevation?: number; // 离地高度（cm）
  passable?: boolean; // 是否可穿行（用于路径规划）
}

// 楼层元素（用于路径规划）
export interface KidataElement {
  type: string; // 元素类型：wall, rectangle, circle, polygon, area, text等
  visible?: boolean; // 是否可见
  passable?: boolean; // 是否可穿行
  points?: Array<{ x: number; z: number }>; // 点坐标（米，3D世界坐标）
  thickness?: number; // 墙体厚度（米）
  radius?: number; // 圆形半径（米）
  opacity?: number; // 透明度
  color?: string; // 颜色
  id?: string; // 元素ID（用于text等类型）
  content?: string; // 文本内容
  fontSize?: number; // 字体大小
  fontFamily?: string; // 字体
  bold?: boolean; // 是否粗体
  italic?: boolean; // 是否斜体
  borderColor?: string; // 边框颜色
  backgroundColor?: string; // 背景颜色
  backgroundOpacity?: number; // 背景透明度
  padding?: number; // 内边距
  elevation?: number; // 离地高度
}

// 楼层图层
export interface KidataLayer {
  id: string;
  name: string;
  visible?: boolean;
  elements: KidataElement[];
}

// 楼层数据
export interface KidataFloor {
  id: string;
  name: string;
  level: number;
  layers: KidataLayer[];
}

interface KidataFormat {
  version: string;
  // 3D 模型不再通过 URL 下载。模型源文件直接嵌入在 `models` 字段里，SDK 会从 kidata 内容中解析。
  dataUrl?: string;
  serverUrl?: string; // 兼容旧版本（已废弃）
  furnitures: KidataFurniture[];
  floors?: KidataFloor[]; // 楼层数据（用于路径规划）
  models?: Record<string, KidataModel>; // 内嵌模型源文件（hash -> 模型内容）
}

/**
 * 内嵌的 3D 模型源文件
 * key = KidataModel.hash（SHA-256）
 * KidataFurniture.modelRef 指向这里
 */
export interface KidataModel {
  hash: string;           // 模型内容 hash（去重 key，SHA-256 前 16 字符）
  ext: string;            // '.obj' | '.glb' | '.gltf' | '.fbx'
  format: 'obj' | 'glb' | 'gltf' | 'fbx';
  data: string;           // base64 编码的源文件二进制
  sourcePath?: string;    // 原始路径（用于调试，可选）
}

/**
 * 获取模型源文件内容并转 base64 + 计算 hash
 * - 内置模型（/KiMap_3D_Model/... 开头）：fetch 拉取
 * - 自定义模型（data: URL 开头）：fetch 拉取（fetch 协议能直接吃 data URL）
 * - 限并发：6
 */
async function fetchModelEntry(
  modelPath: string,
  concurrency: number = 6
): Promise<{ entry: KidataModel; buffer: ArrayBuffer } | null> {
  try {
    const response = await fetch(modelPath);
    if (!response.ok) {
      console.warn(`⚠️ 模型文件获取失败 (${response.status}): ${modelPath}`);
      return null;
    }
    const buffer = await response.arrayBuffer();

    // 推断扩展名
    const lastSlash = modelPath.lastIndexOf('/');
    const fileName = lastSlash >= 0 ? modelPath.substring(lastSlash + 1) : modelPath;
    const dotIndex = fileName.lastIndexOf('.');
    if (dotIndex < 0) {
      console.warn(`⚠️ 模型文件无扩展名，跳过: ${modelPath}`);
      return null;
    }
    const ext = fileName.substring(dotIndex).toLowerCase();

    let format: KidataModel['format'];
    if (ext === '.obj') format = 'obj';
    else if (ext === '.glb') format = 'glb';
    else if (ext === '.gltf') format = 'gltf';
    else if (ext === '.fbx') format = 'fbx';
    else {
      console.warn(`⚠️ 不支持的模型格式 ${ext}，跳过: ${modelPath}`);
      return null;
    }

    // SHA-256 hash（用 WordArray 处理任意二进制）
    const wordArray = CryptoJS.lib.WordArray.create(buffer as any);
    const fullHash = CryptoJS.SHA256(wordArray).toString();
    const hash = fullHash.substring(0, 16);

    // ArrayBuffer -> base64（分块处理大文件）
    const bytes = new Uint8Array(buffer);
    const CHUNK_SIZE = 0x8000;
    let binary = '';
    for (let i = 0; i < bytes.length; i += CHUNK_SIZE) {
      binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK_SIZE)) as any);
    }
    const data = btoa(binary);

    return {
      entry: { hash, ext, format, data, sourcePath: modelPath },
      buffer,
    };
  } catch (err) {
    console.warn(`⚠️ 模型文件获取异常，跳过: ${modelPath}`, err);
    return null;
  }
}

/**
 * 并发限流版的 fetchAll
 */
async function fetchModelsWithConcurrency(
  paths: string[],
  concurrency: number
): Promise<Map<string, KidataModel>> {
  const result = new Map<string, KidataModel>();
  let cursor = 0;

  async function worker(): Promise<void> {
    while (cursor < paths.length) {
      const idx = cursor++;
      const path = paths[idx];
      // 已存在 hash 复用，跳过 fetch
      if (Array.from(result.values()).some(m => m.sourcePath === path)) continue;
      const fetched = await fetchModelEntry(path, concurrency);
      if (fetched) {
        // 同 hash 去重
        if (!result.has(fetched.entry.hash)) {
          result.set(fetched.entry.hash, fetched.entry);
        }
      }
    }
  }

  const workers: Promise<void>[] = [];
  const n = Math.max(1, Math.min(concurrency, paths.length));
  for (let i = 0; i < n; i++) workers.push(worker());
  await Promise.all(workers);

  return result;
}

/**
 * 从项目中提取所有去重后的 model3d 元素的 modelPath
 */
function collectModelPaths(project: Project): string[] {
  const set = new Set<string>();
  project.floors.forEach(floor => {
    floor.layers.forEach(layer => {
      layer.elements.forEach(element => {
        if (element.type === 'model3d') {
          const model = element as Model3DElement;
          if (model.modelPath) set.add(model.modelPath);
        }
      });
    });
  });
  return Array.from(set);
}

/**
 * 简单加密函数（Base64 + 简单混淆）
 * 与SDK的解密逻辑完全匹配
 */
export function encryptKidata(data: string): string {
  try {
    // 1. UTF-8字符串转Latin1（与SDK的escape/unescape对应）
    // 注意：这里必须使用unescape(encodeURIComponent())来匹配SDK的decodeURIComponent(escape())
    const latin1 = unescape(encodeURIComponent(data));
    
    // 2. 第一次Base64编码
    const base64 = btoa(latin1);
    
    // 3. 简单混淆：每个字符与固定值异或
    // 注意：XOR后的结果必须确保是有效的字符串，可以被btoa处理
    const key = 0x5A; // 混淆密钥
    const xorBytes: number[] = [];
    for (let i = 0; i < base64.length; i++) {
      xorBytes.push(base64.charCodeAt(i) ^ key);
    }
    
    // 将字节数组转换为二进制字符串（分块处理，避免调用栈溢出）
    // 每次处理8192个字节，避免String.fromCharCode参数过多
    const CHUNK_SIZE = 8192;
    let encrypted = '';
    for (let i = 0; i < xorBytes.length; i += CHUNK_SIZE) {
      const chunk = xorBytes.slice(i, i + CHUNK_SIZE);
      encrypted += String.fromCharCode(...chunk);
    }
    
    // 4. 第二次Base64编码
    return btoa(encrypted);
  } catch (error) {
    console.error('Kidata加密失败:', error);
    throw error;
  }
}

/**
 * 简单解密函数（用于测试）
 * 与SDK的解密逻辑完全一致
 */
export function decryptKidata(encrypted: string): string {
  try {
    // 1. 第一次Base64解码
    const decoded = atob(encrypted);
    
    // 2. XOR反混淆
    const key = 0x5A;
    let decrypted = '';
    for (let i = 0; i < decoded.length; i++) {
      decrypted += String.fromCharCode(decoded.charCodeAt(i) ^ key);
    }
    
    // 3. 第二次Base64解码
    const base64Decoded = atob(decrypted);
    
    // 4. Latin1转UTF-8
    return decodeURIComponent(escape(base64Decoded));
  } catch (error) {
    console.error('Failed to decrypt kidata:', error);
    return '';
  }
}

/**
 * 测试加密解密是否正确
 */
export function testKidataEncryption(): boolean {
  const testData = {
    version: '1.0',
    serverUrl: 'http://test.com',
    furnitures: [
      {
        id: 'test-1',
        type: 'Sofa',
        position: { x: 1.0, y: 0, z: 2.0 },
        rotation: { x: 0, y: 0, z: 0 },
        scale: { x: 1, y: 1, z: 1 },
        color: '#888888'
      }
    ]
  };
  
  try {
    const jsonStr = JSON.stringify(testData);
    console.log('原始数据:', jsonStr);
    
    const encrypted = encryptKidata(jsonStr);
    console.log('加密后:', encrypted.substring(0, 50) + '...');
    
    const decrypted = decryptKidata(encrypted);
    console.log('解密后:', decrypted);
    
    const match = jsonStr === decrypted;
    console.log('加密解密测试:', match ? '✅ 通过' : '❌ 失败');
    
    return match;
  } catch (error) {
    console.error('测试失败:', error);
    return false;
  }
}

/**
 * 构建 kidata JSON 字符串（包含内嵌模型源文件）
 * 这是发布/导出共用的核心逻辑，mapPublisher 也会复用
 */
export async function buildKidataContentWithModels(project: Project): Promise<{
  jsonStr: string;
  furnituresCount: number;
  modelsCount: number;
}> {
  console.log('🚀 构建 kidata 内容 (buildKidataContentWithModels)');

  // 第一遍：收集所有去重后的 modelPath
  const modelPaths = collectModelPaths(project);
  console.log(`📦 共 ${modelPaths.length} 个不同的3D模型源文件需要嵌入`);

  // 第二遍：并发 fetch 所有模型源文件
  const modelsMap = await fetchModelsWithConcurrency(modelPaths, 6);
  console.log(`✅ 成功嵌入 ${modelsMap.size} 个模型 (去重后)`);

  const furnitures: KidataFurniture[] = [];

  // 遍历所有楼层和图层，提取model3d元素
  project.floors.forEach(floor => {
    floor.layers.forEach(layer => {
      layer.elements.forEach(element => {
        if (element.type === 'model3d') {
          const model = element as Model3DElement;

          // 去掉modelPath中的/KiMap_3D_Model/前缀
          let relativePath = model.modelPath;
          if (relativePath.startsWith('/KiMap_3D_Model/')) {
            relativePath = relativePath.substring('/KiMap_3D_Model/'.length);
          } else if (relativePath.startsWith('KiMap_3D_Model/')) {
            relativePath = relativePath.substring('KiMap_3D_Model/'.length);
          }

          // 从路径中提取文件名（不含扩展名）
          const lastSlashIndex = relativePath.lastIndexOf('/');
          const fileName = lastSlashIndex >= 0 ? relativePath.substring(lastSlashIndex + 1) : relativePath;
          const fileNameWithoutExt = fileName.replace(/\.(obj|glb|gltf|fbx)$/i, '');

          // 计算模型中心点
          const centerX = model.points.reduce((sum, p) => sum + p.x, 0) / model.points.length;
          const centerY = model.points.reduce((sum, p) => sum + p.y, 0) / model.points.length;

          // 转换为3D坐标
          const elevationM = (model.elevation || 0) / 100;
          const position = {
            x: centerX / 100,
            y: elevationM,
            z: centerY / 100,
          };

          // 旋转（度转弧度）
          const rotation = {
            x: (model.rotationX || 0) * Math.PI / 180,
            y: (model.rotation || 0) * Math.PI / 180,
            z: 0,
          };

          // 缩放
          const scale = {
            x: model.scale || 1,
            y: model.scale || 1,
            z: model.scale || 1,
          };

          // 目标尺寸
          const targetSize = {
            width: model.width / 100,
            height: model.height / 1000,
            depth: model.depth / 100,
          };

          // 查找该模型对应的内嵌 hash
          let modelRef: string | undefined;
          for (const [hash, entry] of modelsMap.entries()) {
            if (entry.sourcePath === model.modelPath) {
              modelRef = hash;
              break;
            }
          }

          furnitures.push({
            id: fileNameWithoutExt,
            type: model.modelCategory,
            modelRef,
            position,
            rotation,
            scale,
            color: model.color || '#888888',
            targetSize,
            elevation: model.elevation || 0,
          });
        }
      });
    });
  });
  
  // 提取楼层数据（用于路径规划）
  // 坐标转换：像素 → 米（3D世界坐标）
  const floors: KidataFloor[] = project.floors.map(floor => ({
    id: floor.id,
    name: floor.name,
    level: floor.level,
    layers: floor.layers.map(layer => ({
      id: layer.id,
      name: layer.name,
      visible: layer.visible !== false, // 默认可见
      elements: layer.elements
        .filter(element => {
          // 导出路径规划相关的元素和文本元素
          const relevantTypes = ['wall', 'rectangle', 'circle', 'polygon', 'area', 'text'];
          return relevantTypes.includes(element.type);
        })
        .map(element => {
          const baseData: KidataElement = {
            type: element.type,
            visible: element.visible !== false, // 默认可见
            passable: (element as any).passable === true // 只有明确设置为true才可穿行
          };

          // 转换坐标：像素 → 米（3D世界坐标）
          // 编辑器坐标系：X轴向右，Y轴向下（像素）
          // 3D坐标系：X轴向右，Z轴向前（米）
          const convertPoints = (points: Array<{x: number, y: number}>) => {
            return points.map(p => ({
              x: p.x / 100,  // 像素 → 米
              z: p.y / 100   // Y轴映射到Z轴，像素 → 米
            }));
          };

          // 根据类型添加特定属性
          if (element.type === 'wall') {
            const wallElement = element as any;
            return {
              ...baseData,
              points: convertPoints(wallElement.points || []),
              thickness: (wallElement.thickness || 200) / 1000,  // 毫米 → 米
              opacity: wallElement.opacity !== undefined ? wallElement.opacity : 1,  // 墙面透明度
              color: wallElement.color || '#d1d5db',  // 墙面颜色
              material: wallElement.material || 'concrete'  // 墙面材质
            };
          } else if (element.type === 'text') {
            const textElement = element as any;
            return {
              ...baseData,
              id: textElement.id,
              content: textElement.content || '',
              points: convertPoints(textElement.points || []),
              fontSize: textElement.fontSize || 16,
              color: textElement.color || '#000000',
              fontFamily: textElement.fontFamily || 'Arial, sans-serif',
              bold: textElement.bold || false,
              italic: textElement.italic || false,
              borderColor: textElement.borderColor || '#ADD8E6',
              backgroundColor: textElement.backgroundColor || '#FFFFFF',
              backgroundOpacity: textElement.backgroundOpacity !== undefined ? textElement.backgroundOpacity : 0.8,
              padding: textElement.padding || 8,
              elevation: textElement.elevation || 8500  // mm
            };
          } else if (element.type === 'rectangle' || element.type === 'area' || element.type === 'polygon') {
            return {
              ...baseData,
              points: convertPoints((element as any).points || []),
              opacity: (element as any).opacity !== undefined ? (element as any).opacity : 0.9
            };
          } else if (element.type === 'circle') {
            return {
              ...baseData,
              points: convertPoints((element as any).points || []),
              radius: ((element as any).radius || 500) / 1000,  // 毫米 → 米
              opacity: (element as any).opacity !== undefined ? (element as any).opacity : 0.9
            };
          }

          return baseData;
        })
    }))
  }));

  // 构建kidata数据（包含家具、楼层、内嵌模型）
  const kidataObj: KidataFormat = {
    version: '1.0',
    dataUrl: '',
    furnitures,
    floors,
    models: Object.fromEntries(modelsMap),
  };

  const jsonStr = JSON.stringify(kidataObj, null, 2);
  console.log(`  - 家具数量: ${furnitures.length}`);
  console.log(`  - 楼层数量: ${floors.length}`);
  console.log(`  - 内嵌模型数: ${modelsMap.size}`);
  console.log(`  - kidata文件大小: ${(jsonStr.length / 1024).toFixed(2)} KB`);

  return {
    jsonStr,
    furnituresCount: furnitures.length,
    modelsCount: modelsMap.size,
  };
}

/**
 * 导出项目的3D家具数据为kidata文件（使用指定的ID）
 * 异步：会并发拉取所有用到的3D模型源文件并嵌入到 kidata 中
 */
export async function exportProjectAsKidataWithId(
  project: Project,
  uniqueId: string
): Promise<void> {
  const { jsonStr, furnituresCount, modelsCount } = await buildKidataContentWithModels(project);

  const encrypted = encryptKidata(jsonStr);

  const blob = new Blob([encrypted], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${uniqueId}.kidata`;
  link.click();
  URL.revokeObjectURL(url);

  console.log('✅ Kidata导出成功：');
  console.log(`  - ${uniqueId}.kidata`);
  console.log(`  - 包含 ${furnituresCount} 个3D家具模型`);
  console.log(`  - 包含 ${modelsCount} 个内嵌模型源文件`);
}

/**
 * 导出项目的3D家具数据为kidata文件（自动生成ID）
 * 内部委托给 buildKidataContentWithModels，下载文件并返回生成的 uniqueId
 */
export async function exportProjectAsKidata(project: Project): Promise<string> {
  const { jsonStr, furnituresCount, modelsCount } = await buildKidataContentWithModels(project);

  const encrypted = encryptKidata(jsonStr);

  // 生成文件名（与OBJ文件同名）
  const timestamp = Date.now().toString(36);
  const randomStr = Math.random().toString(36).substring(2, 10);
  const uniqueId = `kimap${timestamp}${randomStr}`;

  const blob = new Blob([encrypted], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${uniqueId}.kidata`;
  link.click();
  URL.revokeObjectURL(url);

  console.log('✅ Kidata导出成功：');
  console.log(`  - ${uniqueId}.kidata`);
  console.log(`  - 包含 ${furnituresCount} 个3D家具模型`);
  console.log(`  - 包含 ${modelsCount} 个内嵌模型源文件`);

  return uniqueId;
}
